/**
 * Listado de una categoria (CAT-01), con los mismos filtros y el mismo scroll
 * infinito que los resultados de busqueda.
 *
 * El `<h1>` es el nombre de la categoria y el titulo SEO sale del backoffice
 * (ADM-08): el administrador puede afinar como aparece en Google sin tocar
 * codigo. Si no lo escribio, se usa el nombre. Los filtros viven en la URL,
 * pero la canonica es la categoria sin filtros.
 */
import { useParams, useSearchParams } from 'react-router-dom';

import type { FiltrosBusqueda } from '@elcarpintero/compartido';

import { ListadoFiltrable } from '../componentes/catalogo/ListadoFiltrable';
import { SinProductosPublicados } from '../componentes/catalogo/SinProductosPublicados';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Migas } from '../componentes/diseno/Migas';
import { useListadoInfinito } from '../hooks/use-listado-infinito';
import { useUltimoDe } from '../hooks/use-ultimo-de';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaListaProductos, esquemaMigas, migasDeCategoria } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { obtenerCategoria } from '../servicios/catalogo.servicio';
import {
  claveFiltros,
  conFiltros,
  escribirFiltros,
  leerFiltros,
  leerPagina,
} from '../utilidades/filtros-busqueda';
import { conteoProductos, numero } from '../utilidades/formato';
import { conPagina, rutas } from '../utilidades/rutas';

export function Categoria() {
  const { slug = '' } = useParams();
  const [parametros, setParametros] = useSearchParams();
  const filtros = leerFiltros(parametros);
  const pagina = leerPagina(parametros);

  const listado = useListadoInfinito(
    JSON.stringify([slug, claveFiltros(filtros)]),
    (pedida, senal) => obtenerCategoria(slug, { pagina: pedida, filtros, senal }),
    pagina,
  );
  // Al filtrar, la cabecera y la barra se quedan con lo ultimo que llego.
  const vista = useUltimoDe(slug, listado.datos);

  if (vista === null) {
    return (
      <div className="contenedor pagina">
        {listado.estado === 'cargando' ? (
          <Cargando que="la categoría" pagina />
        ) : (
          <>
            <Seo
              titulo="Categoría no encontrada"
              descripcion="La categoría que buscas no existe en el catálogo."
              ruta={rutas.categoria(slug)}
              sinIndexar
            />
            <AvisoError error={listado.error} />
          </>
        )}
      </div>
    );
  }

  const { categoria, linea } = vista;
  const migas = migasDeCategoria(linea, categoria);
  // Filtrar vuelve a la primera pagina: `escribirFiltros` no escribe la pagina.
  const filtrar = (siguientes: FiltrosBusqueda) => {
    setParametros(escribirFiltros(siguientes));
  };
  const enlacePagina = (destino: number) =>
    conPagina(conFiltros(rutas.categoria(slug), filtros), destino);
  // Cada pagina es canonica de si misma (sin filtros) y se nombra distinto.
  const dePagina = vista.pagina > 1 ? ` (página ${vista.pagina})` : '';

  return (
    <>
      <Seo
        titulo={`${categoria.tituloSeo ?? categoria.nombre}${dePagina}`}
        descripcion={
          categoria.descripcionSeo ??
          `${numero(vista.totalSinFiltros)} productos de ${categoria.nombre} con stock real y despacho a todo Chile.`
        }
        ruta={conPagina(rutas.categoria(categoria.slug), vista.pagina)}
      />
      <DatosEstructurados
        esquemas={[esquemaMigas(migas), esquemaListaProductos(categoria.nombre, vista.productos)]}
      />

      <div className="contenedor pagina">
        <Migas migas={migas} />

        <header className="encabezado-pag">
          <div>
            <h1>{categoria.nombre}</h1>
            <p className="sub">
              {linea.nombre} · {conteoProductos(vista.total, vista.totalSinFiltros)}
            </p>
          </div>
        </header>

        <ListadoFiltrable
          listado={listado}
          facetas={vista.facetas}
          filtros={filtros}
          onFiltrar={filtrar}
          titulo={`Productos de ${categoria.nombre}`}
          que="los productos"
          donde={`en ${categoria.nombre}`}
          vacio={<SinProductosPublicados />}
          enlacePagina={enlacePagina}
        />
      </div>
    </>
  );
}
